<template>
  <IntercomLayout>
    <!-- Topbar -->
    <template #topbar>
      <GymTopbar
        :business-name="businessName"
        :user="user"
        :global-user="globalUser"
        :search-fn="globalSearch"
        @select-member="handleSelectMember"
        @logout="handleLogout"
      />
    </template>

    <!-- Sidebar -->
    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <!-- Main Content -->
    <div class="member-form-view">
      <!-- Page Header -->
      <div class="page-header">
        <div class="header-left">
          <IntercomButton variant="ghost" size="sm" @click="$router.back()">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            <span>{{ $t('common.back') }}</span>
          </IntercomButton>
          <h1 class="page-title">{{ isEdit ? $t('memberForm.editTitle') : $t('memberForm.newTitle') }}</h1>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <IntercomSpinner />
      </div>

      <!-- Form -->
      <form v-else @submit.prevent="handleSubmit" class="member-form">
        <!-- Basic Information -->
        <IntercomCard class="form-section">
          <h3 class="section-title">{{ $t('memberForm.basicInfo') }}</h3>
          <div class="form-grid">
            <div class="form-field">
              <IntercomTransliterateInput
                v-model="formData.name"
                :label="$t('memberForm.name')"
                required
              />
            </div>
            <div class="form-field">
              <IntercomPhoneInput
                v-model="formData.phone"
                :label="$t('memberForm.phone')"
                :placeholder="$t('memberForm.phonePlaceholder')"
                required
                @update:valid="phoneValid = $event"
              />
            </div>
            <div class="form-field">
              <IntercomEmailInput
                v-model="formData.email"
                :label="$t('memberForm.email')"
                :placeholder="$t('memberForm.emailPlaceholder')"
                @update:valid="emailValid = $event"
              />
            </div>
            <div class="form-field">
              <IntercomDateInput
                v-model="formData.date_of_birth"
                :label="$t('memberForm.dateOfBirth')"
              />
            </div>
            <div class="form-field">
              <label class="field-label">{{ $t('memberForm.gender') }}</label>
              <select v-model="formData.gender" class="select-input">
                <option value="">-</option>
                <option value="male">{{ $t('memberForm.genderMale') }}</option>
                <option value="female">{{ $t('memberForm.genderFemale') }}</option>
                <option value="other">{{ $t('memberForm.genderOther') }}</option>
              </select>
            </div>
          </div>
        </IntercomCard>

        <!-- Contact Information -->
        <IntercomCard class="form-section">
          <h3 class="section-title">{{ $t('memberForm.contactInfo') }}</h3>
          <div class="form-grid">
            <div class="form-field full-width">
              <IntercomInput
                v-model="formData.address"
                :label="$t('memberForm.address')"
                :placeholder="$t('memberForm.addressPlaceholder')"
              />
            </div>
          </div>
        </IntercomCard>

        <!-- Physical Information -->
        <IntercomCard class="form-section">
          <h3 class="section-title">{{ $t('memberForm.physicalInfo', 'Physical Information') }}</h3>

          <!-- Photo Section -->
          <div class="photo-section">
            <label class="field-label">{{ $t('memberForm.photo', 'Photo') }}</label>
            <div class="photo-capture-area">
              <div v-if="photoPreviewUrl" class="photo-preview">
                <img :src="photoPreviewUrl" alt="Member photo" />
                <button type="button" class="photo-remove-btn" @click="removePhoto">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
              <button
                v-else
                type="button"
                class="photo-capture-btn"
                :disabled="isUploadingPhoto"
                @click="showWebcam = true"
              >
                <div v-if="isUploadingPhoto" class="photo-uploading">
                  <IntercomSpinner size="sm" />
                  <span>{{ $t('common.uploading', 'Uploading...') }}</span>
                </div>
                <template v-else>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                    <rect x="3" y="5" width="18" height="14" rx="2" />
                    <circle cx="12" cy="12" r="3" />
                    <path d="M7 5V3h2" />
                    <path d="M15 5V3h2" />
                  </svg>
                  <span>{{ $t('memberForm.capturePhoto', 'Capture Photo') }}</span>
                </template>
              </button>
            </div>
          </div>

          <div class="form-grid" style="margin-top: var(--space-4);">
            <!-- Exercise Experience -->
            <div class="form-field full-width">
              <label class="field-label">{{ $t('memberForm.previousExperience', 'Previous Exercise Experience') }}</label>
              <div class="radio-group">
                <label class="radio-label">
                  <input
                    type="radio"
                    name="exercise_experience"
                    :value="true"
                    v-model="formData.has_exercise_experience"
                    class="radio-input"
                  />
                  <span>{{ $t('common.yes', 'Yes') }}</span>
                </label>
                <label class="radio-label">
                  <input
                    type="radio"
                    name="exercise_experience"
                    :value="false"
                    v-model="formData.has_exercise_experience"
                    class="radio-input"
                  />
                  <span>{{ $t('common.no', 'No') }}</span>
                </label>
              </div>
            </div>

            <!-- Height -->
            <div class="form-field">
              <IntercomInput
                v-model.number="formData.height_cm"
                :label="$t('memberForm.height', 'Height (cm)')"
                :placeholder="$t('memberForm.heightPlaceholder', 'e.g., 170')"
                type="number"
                step="0.1"
                min="0"
                max="300"
              />
            </div>

            <!-- Weight -->
            <div class="form-field">
              <IntercomInput
                v-model.number="formData.weight_kg"
                :label="$t('memberForm.weight', 'Weight (kg)')"
                :placeholder="$t('memberForm.weightPlaceholder', 'e.g., 70')"
                type="number"
                step="0.1"
                min="0"
                max="500"
              />
            </div>
          </div>
        </IntercomCard>

        <!-- WebcamCapture Modal -->
        <WebcamCapture v-model:show="showWebcam" @captured="handlePhotoCaptured" />

        <!-- Emergency Contact -->
        <IntercomCard class="form-section">
          <h3 class="section-title">{{ $t('memberForm.emergencyContact') }}</h3>
          <div class="form-grid">
            <div class="form-field">
              <IntercomInput
                v-model="formData.emergency_contact"
                :label="$t('memberForm.emergencyName')"
              />
            </div>
            <div class="form-field">
              <IntercomPhoneInput
                v-model="formData.emergency_phone"
                :label="$t('memberForm.emergencyPhone')"
                @update:valid="emergencyPhoneValid = $event"
              />
            </div>
          </div>
        </IntercomCard>

        <!-- Enrollment (only for new members) -->
        <IntercomCard v-if="!isEdit" class="form-section">
          <h3 class="section-title">{{ $t('memberForm.enrollment') }}</h3>
          <div class="enrollment-toggle">
            <label class="toggle-label">
              <input type="checkbox" v-model="enrollWithPlan" class="toggle-input" />
              <span>{{ $t('memberForm.enrollWithPlan') }}</span>
            </label>
          </div>

          <div v-if="enrollWithPlan" class="form-grid">
            <div class="form-field">
              <label class="field-label">{{ $t('memberForm.selectPlan') }}</label>
              <select v-model="enrollmentData.plan_id" class="select-input" required>
                <option value="">{{ $t('memberForm.selectPlan') }}</option>
                <option v-for="plan in plans" :key="plan.id" :value="plan.id">
                  {{ plan.name }} - {{ formatCurrency(plan.price) }} ({{ plan.duration_days }} days)
                </option>
              </select>
            </div>
            <div class="form-field">
              <IntercomInput
                v-model.number="enrollmentData.amount"
                :label="$t('memberForm.amount')"
                type="number"
                required
              />
            </div>
            <div class="form-field">
              <label class="field-label">{{ $t('memberForm.paymentMode') }}</label>
              <select v-model="enrollmentData.payment_mode" class="select-input" required>
                <option value="cash">{{ $t('memberForm.cash') }}</option>
                <option value="upi">{{ $t('memberForm.upi') }}</option>
                <option value="card">{{ $t('memberForm.card') }}</option>
                <option value="bank_transfer">{{ $t('memberForm.bankTransfer') }}</option>
              </select>
            </div>
          </div>
        </IntercomCard>

        <!-- Notes -->
        <IntercomCard class="form-section">
          <h3 class="section-title">{{ $t('memberForm.notes') }}</h3>
          <div class="form-field full-width">
            <textarea
              v-model="formData.notes"
              class="notes-input"
              rows="3"
            ></textarea>
          </div>
        </IntercomCard>

        <!-- Error Message -->
        <div v-if="errorMessage" class="error-message">
          {{ errorMessage }}
        </div>

        <!-- Form Actions -->
        <div class="form-actions">
          <IntercomButton variant="secondary" type="button" @click="$router.back()">
            {{ $t('common.cancel') }}
          </IntercomButton>
          <IntercomButton
            variant="primary"
            type="submit"
            :loading="saving"
            :loading-text="isEdit ? $t('memberForm.updating') : $t('memberForm.creating')"
          >
            {{ isEdit ? $t('memberForm.updateMember') : $t('memberForm.createMember') }}
          </IntercomButton>
        </div>
      </form>
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@shared/composables/useAuth.js'
import { useGym } from '../composables/useGym.js'
import { useNavigation } from '../composables/useNavigation.js'
import { IntercomLayout, IntercomSidebar, IntercomCard, IntercomButton, IntercomInput, IntercomSpinner, IntercomPhoneInput, IntercomEmailInput, IntercomTransliterateInput, IntercomDateInput } from '@shared/components/intercom'
import GymTopbar from '../components/GymTopbar.vue'
import WebcamCapture from '../components/WebcamCapture.vue'

const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const { user, globalUser, logout } = useAuth()
const { getMember, createMember, updateMember, listPlans, globalSearch, uploadMemberPhoto } = useGym()

const { navSections } = useNavigation()

// State
const loading = ref(false)
const saving = ref(false)
const errorMessage = ref('')
const plans = ref([])
const enrollWithPlan = ref(false)
const phoneValid = ref(false)
const emailValid = ref(true) // Email is optional, so valid by default
const emergencyPhoneValid = ref(true) // Optional, valid by default
const showWebcam = ref(false)
const photoPreviewUrl = ref('')
const isUploadingPhoto = ref(false)

const formData = reactive({
  name: '',
  phone: '',
  email: '',
  date_of_birth: '',
  gender: '',
  address: '',
  emergency_contact: '',
  emergency_phone: '',
  notes: '',
  // Physical Information
  photo_url: '',
  has_exercise_experience: false,
  height_cm: null,
  weight_kg: null,
})

const enrollmentData = reactive({
  plan_id: '',
  amount: 0,
  payment_mode: 'cash',
})

// Computed
const isEdit = computed(() => !!route.params.id)
const businessName = computed(() => user.value?.business?.name || '')

// Methods
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount)
}

const handlePhotoCaptured = async (blob) => {
  try {
    isUploadingPhoto.value = true
    const response = await uploadMemberPhoto(blob)
    formData.photo_url = response.photo_url
    photoPreviewUrl.value = response.preview_url || URL.createObjectURL(blob)
  } catch (error) {
    console.error('Failed to upload photo:', error)
    errorMessage.value = t('errors.photoUploadFailed', 'Failed to upload photo')
  } finally {
    isUploadingPhoto.value = false
  }
}

const removePhoto = () => {
  formData.photo_url = ''
  photoPreviewUrl.value = ''
}

const fetchMember = async () => {
  if (!isEdit.value) return

  try {
    loading.value = true
    const response = await getMember(route.params.id)
    const member = response.member

    formData.name = member.name || ''
    formData.phone = member.phone || ''
    formData.email = member.email || ''
    formData.date_of_birth = member.date_of_birth ? member.date_of_birth.split('T')[0] : ''
    formData.gender = member.gender || ''
    formData.address = member.address || ''
    formData.emergency_contact = member.emergency_contact || ''
    formData.emergency_phone = member.emergency_phone || ''
    formData.notes = member.notes || ''
    // Physical Information
    formData.photo_url = member.photo_url || ''
    formData.has_exercise_experience = member.has_exercise_experience || false
    formData.height_cm = member.height_cm || null
    formData.weight_kg = member.weight_kg || null
    // Set photo preview if exists
    if (member.photo_url) {
      photoPreviewUrl.value = member.photo_url // Will use presigned URL from API if available
    }
  } catch (error) {
    console.error('Failed to fetch member:', error)
    errorMessage.value = t('errors.notFound')
  } finally {
    loading.value = false
  }
}

const fetchPlans = async () => {
  try {
    const response = await listPlans()
    plans.value = response.plans?.filter(p => p.is_active) || []
  } catch (error) {
    console.error('Failed to fetch plans:', error)
  }
}

const handleSubmit = async () => {
  try {
    saving.value = true
    errorMessage.value = ''

    // Validate required phone
    if (!phoneValid.value) {
      errorMessage.value = t('validation.invalidPhone')
      return
    }

    // Validate optional email (only if provided)
    if (formData.email && !emailValid.value) {
      errorMessage.value = t('validation.invalidEmail')
      return
    }

    // Validate optional emergency phone (only if provided)
    if (formData.emergency_phone && !emergencyPhoneValid.value) {
      errorMessage.value = t('validation.invalidPhone')
      return
    }

    // Build request data - convert empty strings to null for optional fields
    const data = {
      name: formData.name,
      phone: formData.phone,
      email: formData.email || null,
      date_of_birth: formData.date_of_birth ? `${formData.date_of_birth}T00:00:00Z` : null,
      gender: formData.gender || null,
      address: formData.address || null,
      emergency_contact: formData.emergency_contact || null,
      emergency_phone: formData.emergency_phone || null,
      notes: formData.notes || null,
      // Physical Information
      photo_url: formData.photo_url || null,
      has_exercise_experience: formData.has_exercise_experience,
      height_cm: formData.height_cm || null,
      weight_kg: formData.weight_kg || null,
    }

    // Add enrollment data if applicable
    if (!isEdit.value && enrollWithPlan.value && enrollmentData.plan_id) {
      data.plan_id = enrollmentData.plan_id
      data.amount = enrollmentData.amount
      data.payment_mode = enrollmentData.payment_mode
    }

    if (isEdit.value) {
      await updateMember(route.params.id, data)
      router.push(`/members/${route.params.id}`)
    } else {
      const response = await createMember(data)
      router.push(`/members/${response.member.id}`)
    }
  } catch (error) {
    console.error('Failed to save member:', error)
    if (error.response?.status === 409) {
      errorMessage.value = t('errors.duplicate')
    } else {
      errorMessage.value = error.message || t('errors.generic')
    }
  } finally {
    saving.value = false
  }
}

const handleSelectMember = (member) => {
  router.push(`/members/${member.id}`)
}

const handleLogout = async () => {
  await logout()
  router.push('/login')
}

// Watch for plan selection to update amount
watch(() => enrollmentData.plan_id, (planId) => {
  if (planId) {
    const plan = plans.value.find(p => p.id === planId)
    if (plan) {
      enrollmentData.amount = plan.price
    }
  }
})

onMounted(() => {
  fetchMember()
  fetchPlans()
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';

.member-form-view {
  padding: var(--space-6);
  max-width: 900px;
}

.loading-state {
  display: flex;
  justify-content: center;
  padding: var(--space-12);
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-6);
}

.header-left {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  margin: 0;
}

.member-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

.form-section {
  padding: var(--space-5);
}

.section-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  margin: 0 0 var(--space-4) 0;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--space-4);
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.form-field.full-width {
  grid-column: span 2;
}

.field-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-charcoal);
}

.select-input {
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-md);
  font-size: var(--font-size-base);
  background: var(--color-white);
}

.select-input:focus {
  outline: none;
  border-color: var(--color-blue);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.enrollment-toggle {
  margin-bottom: var(--space-4);
}

.toggle-label {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  cursor: pointer;
  font-size: var(--font-size-base);
  color: var(--color-charcoal);
}

.toggle-input {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.notes-input {
  width: 100%;
  padding: var(--space-3);
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-md);
  font-size: var(--font-size-base);
  font-family: inherit;
  resize: vertical;
}

.notes-input:focus {
  outline: none;
  border-color: var(--color-blue);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Photo Capture Styles */
.photo-section {
  margin-bottom: var(--space-2);
}

.photo-capture-area {
  margin-top: var(--space-2);
}

.photo-capture-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  width: 140px;
  height: 140px;
  padding: var(--space-4);
  background: var(--color-gray-50);
  border: 2px dashed var(--color-gray-300);
  border-radius: var(--radius-lg);
  color: var(--color-gray-500);
  font-size: var(--font-size-sm);
  cursor: pointer;
  transition: all 0.15s;
}

.photo-capture-btn:hover:not(:disabled) {
  border-color: var(--color-blue);
  color: var(--color-blue);
  background: rgba(59, 130, 246, 0.05);
}

.photo-capture-btn:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.photo-uploading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
}

.photo-preview {
  position: relative;
  width: 140px;
  height: 140px;
  border-radius: var(--radius-lg);
  overflow: hidden;
  border: 2px solid var(--color-gray-200);
}

.photo-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.photo-remove-btn {
  position: absolute;
  top: var(--space-1);
  right: var(--space-1);
  width: 24px;
  height: 24px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.6);
  border: none;
  border-radius: 50%;
  color: white;
  cursor: pointer;
  transition: background 0.15s;
}

.photo-remove-btn:hover {
  background: rgba(220, 38, 38, 0.9);
}

/* Radio Group Styles */
.radio-group {
  display: flex;
  gap: var(--space-6);
}

.radio-label {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  cursor: pointer;
  font-size: var(--font-size-base);
  color: var(--color-charcoal);
}

.radio-input {
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: var(--color-blue);
}

.error-message {
  padding: var(--space-3) var(--space-4);
  background: var(--color-error-bg);
  border: 1px solid var(--color-error);
  border-radius: var(--radius-md);
  color: var(--color-error);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
}

@media (max-width: 768px) {
  .member-form-view {
    padding: var(--space-4);
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .form-field.full-width {
    grid-column: span 1;
  }
}
</style>
